import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Fetches exchange rates for a list of currencies relative to toCurrency.
 *
 * @param {object} params
 * @param {string|number} params.ledgerId
 * @param {string[]} params.currencies  - e.g. ['CAD', 'CNY']
 * @param {string}   params.toCurrency  - e.g. 'USD'
 *
 * @returns {{ rates: Record<string, number>, loading: boolean, error: any }}
 *   rates keys that equal toCurrency will have value 1.0.
 *   If all currencies equal toCurrency, rates is {} (no fetch needed).
 */
export function useBudgetRates({ ledgerId, currencies = [], toCurrency }) {
  const { api } = useAuth();
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stable key so we don't re-fetch on every render
  const fromCurrencies = useMemo(() => {
    const unique = [...new Set(currencies.filter(Boolean))];
    // Only fetch rates for currencies that differ from toCurrency
    return unique.filter((c) => c !== toCurrency);
  }, [currencies, toCurrency]);

  const keyRef = useRef('');
  const newKey = `${ledgerId}|${fromCurrencies.join(',')}|${toCurrency}`;

  useEffect(() => {
    if (!ledgerId || !toCurrency || fromCurrencies.length === 0) {
      setRates({});
      return;
    }
    // Skip if nothing changed
    if (keyRef.current === newKey) return;
    keyRef.current = newKey;

    let cancelled = false;
    const fetchRates = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          from_currencies: fromCurrencies.join(','),
          to_currency: toCurrency,
        });
        const res = await api.get(
          `/ledger/ledgers/${ledgerId}/budget/exchange-rates?${params}`
        );
        if (!cancelled) setRates(res.data.rates ?? {});
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRates();
    return () => { cancelled = true; };
  }, [newKey, ledgerId, fromCurrencies, toCurrency, api]);

  return { rates, loading, error };
}
